<template>
  <nk-dropdown v-model="dropdownValue" :data="dropdownData" :options="settings.dropdownOptions" :disabled="disabled ? true : null" ref="input" :tabindex="tabindex"></nk-dropdown>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import _ from 'lodash'
  import $sc from '~/plugins/shortcut.collection.js'
  import SalaryItemList from '~/views/salary/salary-item/salary-item-list.vue'
  import SalaryItemModify from '~/views/salary/salary-item/salary-item-modify.vue'
  import { IResponseList } from '~/interfaces/base'
  import { SalaryItem } from '~/classes/salary/salary-item'
  import { post } from '~/plugins/nikan-api'
  import Endpoints from '@nikan/endpoints'
  import { SalaryItemActionTypes, SalaryItemFillTypes, SalaryItemTypes } from '~/enums/salary'

  export default defineComponent({
    props: {
      modelValue: { type: Object as PropType<SalaryItem | null>, default: null },
      options: {},
      tabindex: {},
      disabled: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
      return {
        settings: {
          filterByTypes: [] as SalaryItemTypes[],
          filterByfillTypes: [] as SalaryItemFillTypes[],
          filterByActionTypes: [] as SalaryItemActionTypes[],
          dropdownOptions: {
            rowIdentifier: 'id',
            remoteSearch: false,
            placeholder: this.$t('select-it'),
            selectorWidth: 300,
            searchableFields: ['name'],
            display: (item: SalaryItem) => item.name,
            buttons: [
              {
                title: this.$t('salary.salary-item.management'),
                shortcut: $sc.find('enter'),
                isPrimary: true,
                element: () => this.$el,
                icon: 'list-ul',
                handle: () => {
                  this.showSalaryItemList()
                },
              },
              {
                title: this.$t('new'),
                shortcut: $sc.find('ctrl-insert'),
                isPrimary: false,
                element: () => this.$el,
                icon: 'plus',
                handle: () => {
                  this.showSalaryItemModify()
                }
              },
            ],
          },
        },
        localValue: null as SalaryItem | null,
        dropdownData: [] as SalaryItem[]
      }
    },
    computed: {
      dropdownValue: {
        get(): SalaryItem | null {
          return this.localValue
        },
        set(val: SalaryItem): void {
          this.$emit('update:modelValue', val)
          this.localValue = val
        },
      },
    },
    watch: {
      modelValue: {
        handler(val, oldVal) {
          this.localValue = val
        },
      },
      options: {
        immediate: true,
        deep: true,
        handler(val) {
          this.settings = _.merge(this.settings, val)
        },
      },
    },
    mounted() {
      this.localValue = this.modelValue
      this.fetchSalaryItems()
    },
    methods: {
      fetchSalaryItems() {
        const wait = this.$wait(this.$el, { size: 'small' })
        post(Endpoints.getUrl('salaryItem.list'), null, null, { preventConcurrent: false })
          .then((res: IResponseList<SalaryItem>) => {

            let data: SalaryItem[] = res.data.items

            if (this.settings.filterByTypes.length)
              data = data.filter((x: SalaryItem) => this.settings.filterByTypes.includes(x.type))

            if (this.settings.filterByfillTypes.length)
              data = data.filter((x: SalaryItem) => this.settings.filterByfillTypes.includes(x.fillType))

            if (this.settings.filterByActionTypes.length)
              data = data.filter((x: SalaryItem) => this.settings.filterByActionTypes.includes(x.actionType))

            this.dropdownData = data

          })
          .finally(() => {
            wait.end()
          })
      },
      showSalaryItemModify() {
        this.showModal({ component: SalaryItemModify })
          .then((result) => {
            this.dropdownValue = result
            this.$refs.input.focusAndSelect()
            this.fetchSalaryItems()
          })
          .catch(x => { })
      },
      showSalaryItemList() {
        this.showModal({
          component: SalaryItemList,
          options: {
            filterByTypes: this.settings.filterByTypes,
            filterByfillTypes: this.settings.filterByfillTypes,
            filterByActionTypes: this.settings.filterByActionTypes,
          }
        })
          .then((result) => {
            this.dropdownValue = result
            this.$refs.input.focusAndSelect()
          })
          .catch(x => { })
      },
    },
  })
</script>
<style></style>
